import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'https://cannes.direzione-marketingtools.workers.dev/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name?: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  getAuthHeaders: () => Record<string, string>;
  checkAuth: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();

          if (!response.ok) {
            set({ error: data.error || 'Login failed', isLoading: false });
            return false;
          }

          set({ user: data.user, token: data.token, isLoading: false });
          return true;
        } catch (error) {
          console.error('Login error:', error);
          set({ error: 'Network error - please check your connection', isLoading: false });
          return false;
        }
      },

      register: async (email: string, password: string, name?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({ email, password, name }),
          });

          const data = await response.json();

          if (!response.ok) {
            set({ error: data.error || 'Registration failed', isLoading: false });
            return false;
          }

          set({ user: data.user, token: data.token, isLoading: false });
          return true;
        } catch (error) {
          console.error('Register error:', error);
          set({ error: 'Network error - please check your connection', isLoading: false });
          return false;
        }
      },

      logout: () => {
        set({ user: null, token: null, error: null });
      },

      clearError: () => {
        set({ error: null });
      },

      getAuthHeaders: (): Record<string, string> => {
        const { token } = get();
        return token ? { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        } : {
          'Accept': 'application/json',
        };
      },

      checkAuth: async () => {
        const { token } = get();
        if (!token) return false;

        try {
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: get().getAuthHeaders(),
          });

          if (!response.ok) {
            set({ token: null, user: null });
            return false;
          }

          return true;
        } catch (error) {
          console.error('Auth check error:', error);
          return false;
        }
      },
    }),
    {
      name: 'cannes-auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token }),
    }
  )
);
